import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Typography
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigationInterceptorContext } from "../../../../../contexts/useNavigationInterceptorContext";
import { useFreelancerContext } from "../../../../../contexts/useFreelancerContext";
import { useFreelancerOnboardingFormSave } from "../../../../../hooks/useFreelancerOnboardingFormsSave";

const UnsavedChangesDialog: React.FC = () => {
	const { showDialog, confirmNavigation, cancelNavigation } =
		useNavigationInterceptorContext();
	const { saveOnboardingFormStepAsync, saveOnboardingFormAsync, isSaveProgressPending } =
		useFreelancerOnboardingFormSave();
	const { nextOnboardingFormStep, currentOnboardingFormStep } = useFreelancerContext();
	const { t } = useTranslation();

	const handleSaveAndContinue = async () => {
		try {
			await saveOnboardingFormAsync(nextOnboardingFormStep);
		} catch {
			// The save failed (the save hooks already showed the error snackbar): keep the
			// dialog open instead of navigating away with unsaved content.
			return;
		}

		confirmNavigation();
	};

	const handleDiscardChanges = async () => {
		try {
			await saveOnboardingFormStepAsync(nextOnboardingFormStep);
		} catch {
			return;
		}

		confirmNavigation();
	};

	const handleCancel = () => {
		cancelNavigation();
	};

	return (
		<Dialog
			open={showDialog}
			onClose={handleCancel}
			aria-labelledby="unsaved-changes-dialog-title"
			aria-describedby="unsaved-changes-dialog-description"
			maxWidth="md"
			fullWidth
			slotProps={{
				paper: {
					sx: {
						borderRadius: 2,
						boxShadow: 3
					}
				}
			}}
		>
			<DialogTitle id="unsaved-changes-dialog-title">
				<Typography variant="h3" component="span">
					{t("unsavedChangesDialog.title")}
				</Typography>
			</DialogTitle>
			<DialogContent>
				<DialogContentText id="unsaved-changes-dialog-description">
					{t("unsavedChangesDialog.message")}
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<>
					<Button
						disabled={isSaveProgressPending}
						onClick={handleCancel}
						color="secondary"
						disableRipple
					>
						{t("unsavedChangesDialog.cancel")}
					</Button>
					{nextOnboardingFormStep <= currentOnboardingFormStep && (
						<Button
							disabled={isSaveProgressPending}
							onClick={handleDiscardChanges}
							color="secondary"
							variant="outlined"
							disableRipple
						>
							{t("unsavedChangesDialog.discard")}
						</Button>
					)}
					<Button
						onClick={handleSaveAndContinue}
						color="secondary"
						variant="contained"
						disabled={isSaveProgressPending}
						disableRipple
					>
						{isSaveProgressPending
							? t("unsavedChangesDialog.saving")
							: t("unsavedChangesDialog.saveAndContinue")}
					</Button>
				</>
			</DialogActions>
		</Dialog>
	);
};

export default UnsavedChangesDialog;
